import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { ListMusic, Sparkles, Play, Tv, Mic2 } from "lucide-react";
import { QueueItemCard } from "./QueueItemCard";
import { PlayerModal } from "../player/PlayerModal";
import { usePartyStore } from "../../stores/partyStore";
import { useAuthStore } from "../../stores/authStore";
import { useIsMobile } from "../../hooks/useIsMobile";
import { reorderQueue, removeSongFromQueue } from "../../lib/firebase";
import type { QueueSong } from "../../types";

export const SortableQueueList: React.FC = () => {
  const { currentParty, userRole, queue, setQueue, setTVMode } = usePartyStore();
  const { user } = useAuthStore();
  const isMobile = useIsMobile();

  const [activePlaySong, setActivePlaySong] = useState<QueueSong | null>(null);

  const nowSingingSong = queue.length > 0 ? queue[0] : null;

  // Keep activePlaySong in sync with live queue progression
  useEffect(() => {
    if (activePlaySong) {
      const stillInQueue = queue.find((q) => q.id === activePlaySong.id);
      if (!stillInQueue) {
        // Active song finished and was removed, auto-advance to next song in queue
        if (queue.length > 0) {
          setActivePlaySong(queue[0]);
        } else {
          setActivePlaySong(null);
        }
      }
    }
  }, [queue, activePlaySong]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !currentParty) return;

    const oldIndex = queue.findIndex((item) => item.id === active.id);
    const newIndex = queue.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistic local state update
    const reordered = arrayMove(queue, oldIndex, newIndex);
    setQueue(reordered);

    try {
      const currentOrder = queue.map((q) => q.id);
      await reorderQueue(
        currentParty.id,
        active.id as string,
        oldIndex,
        newIndex,
        currentOrder
      );
    } catch (err) {
      console.error("Queue reorder failed, reverting:", err);
      setQueue(queue);
    }
  };

  const handleDelete = async (videoId: string) => {
    if (!currentParty) return;
    try {
      // Optimistic local state update
      const nextQueue = queue.filter((q) => q.id !== videoId);
      setQueue(nextQueue);
      if (activePlaySong?.id === videoId) {
        setActivePlaySong(nextQueue.length > 0 ? nextQueue[0] : null);
      }

      await removeSongFromQueue(currentParty.id, videoId);
    } catch (err: any) {
      alert(`Could not remove song: ${err.message}`);
    }
  };

  const handlePlaySong = (song: QueueSong) => {
    setActivePlaySong(song);
  };

  if (queue.length === 0) {
    return (
      <div className="bg-card border border-dashed border-border rounded-3xl p-10 text-center space-y-4 shadow-sm">
        <div className="w-16 h-16 mx-auto rounded-3xl bg-primary/10 flex items-center justify-center text-primary">
          <ListMusic className="w-8 h-8" />
        </div>
        <div className="space-y-1">
          <h3 className="text-xl font-bold font-heading text-text-main">The Queue is Empty</h3>
          <p className="text-xs text-text-muted max-w-xs mx-auto">
            Switch to the <strong>Add a Song</strong> tab to search YouTube and add songs to the party queue!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Active "Now Singing / Up Next" Highlight Card */}
      {nowSingingSong && (
        <div className="bg-card p-4 sm:p-5 rounded-3xl border border-primary/40 shadow-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative overflow-hidden">
          <div className="absolute top-0 left-0 bottom-0 w-1.5 bg-primary" />
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 pl-1">
            <div className="relative w-16 h-12 sm:w-20 sm:h-14 rounded-2xl overflow-hidden bg-black shrink-0 shadow-md">
              <img
                src={nowSingingSong.thumbnailUrl}
                alt={nowSingingSong.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <Mic2 className="w-5 h-5 text-white" />
              </div>
            </div>

            <div className="min-w-0 space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase font-bold tracking-wider text-white bg-primary px-2.5 py-0.5 rounded-full shadow-sm">
                  Up Next #1
                </span>
                <span className="text-xs text-text-muted truncate">
                  Singer: <strong className="text-text-main font-semibold">{nowSingingSong.addedByUserName}</strong>
                </span>
              </div>
              <h4 className="text-sm sm:text-base font-bold text-text-main truncate leading-tight">
                {nowSingingSong.title}
              </h4>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
            {/* Play in App Modal (Host only) */}
            {userRole === "HOST" && (
              <button
                onClick={() => handlePlaySong(nowSingingSong)}
                className="py-2.5 px-4 rounded-2xl bg-primary hover:bg-primary-hover text-white text-xs font-bold flex items-center gap-2 transition-all shadow-md shadow-primary/25 cursor-pointer"
                title="Play this song in popup player"
              >
                <Play className="w-4 h-4 fill-white" />
                <span>Play Song</span>
              </button>
            )}

            {/* Open Full TV Screen (Desktop / Tablet only) */}
            {!isMobile && (
              <button
                onClick={() => setTVMode(true)}
                className="py-2.5 px-3.5 rounded-2xl bg-background border border-border hover:border-accent text-accent text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
                title="Launch dedicated TV display mode"
              >
                <Tv className="w-4 h-4" />
                <span>TV Screen</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Queue Header info */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-semibold text-text-muted flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-accent" />
          Drag handles to reorder songs for the whole party
        </span>
        <span className="text-xs font-mono font-bold text-accent">
          {queue.length} {queue.length === 1 ? "song" : "songs"}
        </span>
      </div>

      {/* Drag and drop sortable list */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={queue.map((q) => q.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2.5">
            {queue.map((song, index) => (
              <QueueItemCard
                key={song.id}
                song={song}
                index={index}
                currentUserId={user?.uid}
                userRole={userRole}
                onDelete={handleDelete}
                onPlayNow={handlePlaySong}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* In-App Player Modal */}
      <PlayerModal
        isOpen={Boolean(activePlaySong)}
        onClose={() => setActivePlaySong(null)}
        song={activePlaySong}
      />
    </div>
  );
};
